import { Logger } from '@suntex.core/logging';

import {
  type CanvasAccessibility,
  type TabOrderEntry,
  createCanvasAccessibility,
} from './canvasAccessibility';

/** Half-width of the fallback square used for controls with no CreateJS bounds. */
const FALLBACK_HALF_SIZE = 40;

const logger = Logger.for(['a11y', 'tabAccessibility']);

export interface InitTabAccessibilityOptions {
  appName: string;
  stage: createjs.Stage;
  getTabOrder: () => TabOrderEntry[];
  /**
   * Registers the debounced rebuild callback to fire on source changes;
   * returns an unsubscribe called on teardown.
   */
  subscribe: (rebuild: () => void) => () => void;
  /** Debounce, in ms, covering the source's animations before rebuilding. */
  rebuildDelayMs: number | (() => number);
  /**
   * Draw a generic rounded-rectangle outline+glow around the focused entry.
   * Intended for sources without their own hover/focus animation.
   */
  focusHighlight?: boolean;
  getFallbackBounds?: (obj: createjs.DisplayObject) => createjs.Rectangle;
  onEntryFocus?: (obj: createjs.DisplayObject) => void;
  onEntryBlur?: (obj: createjs.DisplayObject) => void;
  /** Reports any adapter creation/rebuild failure asynchronously. Defaults to error-level logging. */
  onError?: (error: unknown) => void;
}

export interface TabAccessibilityHandle {
  /** Tears down the adapter and stops rebuilding. Idempotent. */
  dispose: () => void;
  /**
   * Resolves once the initial creation attempt settles. Never rejects (failures
   * go to `onError`). Callers that fire-and-forget can ignore it; tests await it.
   */
  ready: Promise<void>;
}

function defaultFallbackBounds(): createjs.Rectangle {
  return new createjs.Rectangle(
    -FALLBACK_HALF_SIZE,
    -FALLBACK_HALF_SIZE,
    FALLBACK_HALF_SIZE * 2,
    FALLBACK_HALF_SIZE * 2,
  );
}

/**
 * Wire a canvas source (game or hub) to keyboard tab accessibility: creates
 * the adapter (or, if there are no interactive controls yet, defers creation)
 * and registers a debounced rebuild via `subscribe` to fire on state changes.
 * A source with an initially empty tab order (e.g. controls disabled while
 * progress loads) may still populate one later. Returns a handle whose
 * `dispose` tears down the adapter and stops rebuilding (call it on unmount)
 * and whose `ready` resolves after the first creation attempt. Creation runs
 * asynchronously; any creation/rebuild failure is reported through `onError`.
 */
export function initTabAccessibility(
  options: InitTabAccessibilityOptions,
): TabAccessibilityHandle {
  const {
    appName,
    stage,
    getTabOrder,
    subscribe,
    rebuildDelayMs,
    focusHighlight = true,
    getFallbackBounds = defaultFallbackBounds,
    onEntryFocus,
    onEntryBlur,
    onError = (error: unknown) =>
      logger.error('Tab accessibility sync failed', () => ({ appName }), error),
  } = options;

  let controller: CanvasAccessibility | null = null;
  let disposed = false;
  // Read through a function so the post-await recheck in `sync` is not narrowed
  // to a constant: `dispose` can flip the flag while creation is in flight.
  function isDisposed(): boolean {
    return disposed;
  }

  // One idempotent operation: create the adapter on first controls, then rebuild
  // it on later changes. createCanvasAccessibility returns null while the tab
  // order is still empty, so creation is re-attempted on each rebuild the source
  // triggers until controls appear.
  async function sync(): Promise<void> {
    if (isDisposed()) return;
    if (controller) {
      controller.rebuild();
      return;
    }
    const created = await createCanvasAccessibility({
      appName,
      stage,
      getTabOrder,
      focusHighlight,
      getFallbackBounds,
      onEntryFocus,
      onEntryBlur,
    });
    if (isDisposed()) {
      created?.dispose();
      return;
    }
    controller = created;
  }

  // Serialize every sync onto a single chain so overlapping attempts can't
  // double-create or drop a rebuild that lands mid-initialization, and report
  // failures instead of leaving an unhandled rejection.
  let tail: Promise<void> = Promise.resolve();
  function enqueueSync(): Promise<void> {
    tail = tail.then(sync).catch((error: unknown) => {
      if (!isDisposed()) onError(error);
    });
    return tail;
  }

  // Debounce covering the source's animations before rebuilding, recomputed on
  // each schedule so a caller can vary it (e.g. by animation speed setting).
  let rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleRebuild(): void {
    if (isDisposed()) return;
    if (rebuildTimer) clearTimeout(rebuildTimer);
    const delay =
      typeof rebuildDelayMs === 'function' ? rebuildDelayMs() : rebuildDelayMs;
    rebuildTimer = setTimeout(() => void enqueueSync(), delay);
  }

  const unsubscribe = subscribe(scheduleRebuild);
  const ready = enqueueSync();
  scheduleRebuild();

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (rebuildTimer) clearTimeout(rebuildTimer);
    rebuildTimer = null;
    unsubscribe();
    controller?.dispose();
    controller = null;
  }

  return { dispose, ready };
}
